import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { TierBadge } from "./MatterProcessingStatusStyled";

/**
 * Matter tier badge — Matthew's three-tier matter classification
 * ("We'll need an indicator on the Matter somewhere signifying the tier —
 * worst, middle, best", 2026-07-11).
 *
 * The component is strictly derived state: callers pass the backend's
 * detail-only `matter_tier` field (dms_backend, MatterDetailView) and it
 * renders one small pill next to the processing banner:
 *
 *   "ai"              (best)   → green  "AI-processed"
 *   "legacy_sorted"   (middle) → blue   "Legacy — sorted claim file"
 *   "legacy_unsorted" (worst)  → gray   "Legacy — unsorted"
 *   null / absent / unknown    → renders NOTHING
 *
 * The null contract is load-bearing: list payloads and older backends
 * don't ship `matter_tier`, and a matter with no documents (or only
 * fresh uploads still mid-pipeline) has no tier yet — in all of those
 * cases the matter page must render byte-identical to today. Unknown
 * future values also render nothing rather than a mislabeled badge.
 */

const TIER_CONTENT = {
  ai: {
    label: "AI-processed",
    tooltip:
      "This matter's documents have been processed by the AI pipeline — full text, categories, and knowledge graph are available.",
  },
  legacy_sorted: {
    label: "Legacy — sorted claim file",
    tooltip:
      "This matter has organized legacy files including a codesheet. The AI can build its claim spine from the newest codesheet when documents are processed.",
  },
  legacy_unsorted: {
    label: "Legacy — unsorted",
    tooltip:
      "This matter has legacy files but no sorted claim file. New documents will be processed without a claim spine until a codesheet arrives.",
  },
};

/**
 * Map the backend `matter_tier` value → variant key, or null when there
 * is nothing to render. Exported (and unit-tested) so the "unknown
 * values render nothing" contract has a single authoritative
 * implementation.
 */
export const resolveMatterTierVariant = (tier) =>
  Object.prototype.hasOwnProperty.call(TIER_CONTENT, tier) ? tier : null;

const MatterTierBadge = ({ tier = null, className }) => {
  const variant = resolveMatterTierVariant(tier);
  if (!variant) return null;

  const { label, tooltip } = TIER_CONTENT[variant];

  return (
    <Tooltip title={tooltip} placement="top">
      <TierBadge
        $tier={variant}
        className={className}
        role="img"
        aria-label={`Matter tier: ${label}`}
        data-testid={`matter-tier-badge-${variant}`}
      >
        {label}
      </TierBadge>
    </Tooltip>
  );
};

MatterTierBadge.propTypes = {
  /** Backend `matter_tier` value — null/undefined/unknown renders nothing. */
  tier: PropTypes.oneOf(["ai", "legacy_sorted", "legacy_unsorted", null]),
  className: PropTypes.string,
};

export default MatterTierBadge;
